import { useState, useEffect } from 'react'
import { PageHeader } from '../../../components/ui/PageHeader'
import { Spinner } from '../../../components/ui/Spinner'
import { Alert } from '../../../components/ui/Alert'
import { inputClass, selectClass } from '../../../components/ui/FormField'
import { api } from '../../../api/axios'
import { useAuth } from '../../../contexts/AuthContext'
import { useAtiradores } from '../../atiradores/hooks/useAtiradores'

// ── Tipos ────────────────────────────────────────────────────────────────────

interface Arma {
  idarma: number
  tipo: string
  marca: string
  modelo: string
  calibre: string
  numero_sigma?: string | null
}

interface EventoHabitualidade {
  idhabitualidade?: number   // presente ao editar
  evento: string
  nivel: string
  data_inicio: string
  data_fim: string
  grupo: string
  modalidade: string
  quantidade_armas: string
  idarma: string
  qtd_municao: string
  classificacao: string
  data_participacao: string
  hora_participacao: string
}

interface Habitualidade {
  idhabitualidade: number
  tipo_evento: 'prova' | 'treino'
  desta_entidade: boolean
  evento: string | null
  nivel: string | null
  data_inicio: string | null
  data_fim: string | null
  grupo: string | null
  modalidade: string | null
  quantidade_armas: number | null
  idarma: number | null
  qtd_municao: number | null
  classificacao: number | null
  data_participacao: string | null
  hora_participacao: string | null
  arma_tipo?: string | null
  arma_marca?: string | null
  arma_modelo?: string | null
  arma_calibre?: string | null
  arma_numero_sigma?: string | null
}

// ── Constantes ────────────────────────────────────────────────────────────────

const NIVEIS = ['Municipal', 'Estadual', 'Nacional', 'Internacional', 'Treino']

function emptyEvento(): EventoHabitualidade {
  return {
    evento: '',
    nivel: '',
    data_inicio: '',
    data_fim: '',
    grupo: '',
    modalidade: '',
    quantidade_armas: '1',
    idarma: '',
    qtd_municao: '',
    classificacao: '',
    data_participacao: '',
    hora_participacao: '',
  }
}

// ── Componente Field inline ────────────────────────────────────────────────
function Field({ label, children, required }: { label: string; children: React.ReactNode; required?: boolean }) {
  return (
    <div>
      <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">
        {label}{required && <span className="text-red-500"> *</span>}
      </label>
      {children}
    </div>
  )
}

// ── Pagina Principal ──────────────────────────────────────────────────────────

export function HabitualidadePage() {
  const { hasRole } = useAuth()
  const isAdmin = hasRole('admin')

  // Selecao de atirador (admin)
  const [selAtirador, setSelAtirador] = useState<number | ''>('')
  const [searchAtirador, setSearchAtirador] = useState('')
  const { data: atiradoresData } = useAtiradores(
    isAdmin && searchAtirador.length >= 3 ? { nome: searchAtirador, limit: 20 } : {},
  )
  const atiradores = atiradoresData?.data ?? []

  // Armas do atirador selecionado
  const [armas, setArmas] = useState<Arma[]>([])

  // Lista de habitualidades
  const [habitualidades, setHabitualidades] = useState<Habitualidade[]>([])
  const [loading, setLoading] = useState(false)
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null)

  // Form principal
  const [tipoEvento, setTipoEvento] = useState<'prova' | 'treino'>('treino')
  const [destaEntidade, setDestaEntidade] = useState(false)
  const [comprovante, setComprovante] = useState<File | null>(null)
  const [eventos, setEventos] = useState<EventoHabitualidade[]>([emptyEvento()])

  // Edicao
  const [editando, setEditando] = useState(false)
  const [editId, setEditId] = useState<number | null>(null)

  // Ano filtro
  const [ano, setAno] = useState<number>(new Date().getFullYear())
  const [salvando, setSalvando] = useState(false)

  const podeCarregar = !isAdmin || !!selAtirador

  // Carregar armas e habitualidades quando atirador mudar
  useEffect(() => {
    if (!podeCarregar) {
      setArmas([])
      setHabitualidades([])
      return
    }
    const params = isAdmin && selAtirador ? `?idatirador=${selAtirador}` : ''
    api.get<{ data: Arma[] }>(`/publico/minhas-armas${params}`)
      .then(r => setArmas(r.data.data ?? []))
      .catch(() => setArmas([]))
  }, [selAtirador, podeCarregar, isAdmin])

  useEffect(() => {
    if (!podeCarregar) { setHabitualidades([]); return }
    loadHabitualidades()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selAtirador, ano, podeCarregar])

  async function loadHabitualidades() {
    setLoading(true)
    try {
      const params: Record<string, string> = { ano: String(ano) }
      if (isAdmin && selAtirador) params.idatirador = String(selAtirador)
      const r = await api.get<{ data: Habitualidade[] }>('/habitualidade', { params })
      setHabitualidades(r.data.data ?? [])
    } catch {
      setHabitualidades([])
    } finally {
      setLoading(false)
    }
  }

  function addEvento() {
    setEventos(prev => [...prev, emptyEvento()])
  }

  function removeEvento(idx: number) {
    setEventos(prev => prev.filter((_, i) => i !== idx))
  }

  function setEvento(idx: number, field: keyof EventoHabitualidade, value: string) {
    setEventos(prev => prev.map((e, i) => i === idx ? { ...e, [field]: value } : e))
  }

  function resetForm() {
    setTipoEvento('treino')
    setDestaEntidade(false)
    setComprovante(null)
    setEventos([emptyEvento()])
    setEditando(false)
    setEditId(null)
  }

  function openEdit(hab: Habitualidade) {
    setEditando(true)
    setEditId(hab.idhabitualidade)
    setTipoEvento(hab.tipo_evento)
    setDestaEntidade(hab.desta_entidade)
    setEventos([{
      idhabitualidade: hab.idhabitualidade,
      evento: hab.evento ?? '',
      nivel: hab.nivel ?? '',
      data_inicio: hab.data_inicio ?? '',
      data_fim: hab.data_fim ?? '',
      grupo: hab.grupo ?? '',
      modalidade: hab.modalidade ?? '',
      quantidade_armas: String(hab.quantidade_armas ?? 1),
      idarma: hab.idarma ? String(hab.idarma) : '',
      qtd_municao: hab.qtd_municao ? String(hab.qtd_municao) : '',
      classificacao: hab.classificacao ? String(hab.classificacao) : '',
      data_participacao: hab.data_participacao ?? '',
      hora_participacao: hab.hora_participacao ?? '',
    }])
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  async function handleDelete(id: number) {
    if (!window.confirm('Excluir este registro de habitualidade?')) return
    try {
      await api.delete(`/habitualidade/${id}`)
      setAlert({ type: 'success', message: 'Habitualidade excluida.' })
      await loadHabitualidades()
    } catch (err: any) {
      setAlert({ type: 'error', message: err?.response?.data?.error || 'Erro ao excluir' })
    }
  }

  async function handleSalvar(e: React.FormEvent) {
    e.preventDefault()
    setAlert(null)
    setSalvando(true)
    try {
      // Upload do comprovante se houver
      let comprovanteNome: string | null = null
      if (comprovante) {
        const fd = new FormData()
        fd.append('file', comprovante)
        // Tenta upload - se nao tiver endpoint, apenas ignora gracefully
        try {
          const ur = await api.post('/relatorios/upload', fd)
          comprovanteNome = ur.data?.filename ?? null
        } catch {
          comprovanteNome = comprovante.name
        }
      }

      if (editando && editId) {
        // Editar um registro
        const ev = eventos[0]
        const payload = {
          tipo_evento: tipoEvento,
          desta_entidade: destaEntidade,
          evento: ev.evento || null,
          nivel: ev.nivel || null,
          data_inicio: ev.data_inicio || null,
          data_fim: ev.data_fim || null,
          grupo: ev.grupo || null,
          modalidade: ev.modalidade || null,
          quantidade_armas: ev.quantidade_armas ? parseInt(ev.quantidade_armas) : null,
          idarma: ev.idarma ? parseInt(ev.idarma) : null,
          qtd_municao: ev.qtd_municao ? parseInt(ev.qtd_municao) : null,
          classificacao: ev.classificacao ? parseInt(ev.classificacao) : null,
          data_participacao: ev.data_participacao || null,
          hora_participacao: ev.hora_participacao || null,
          comprovante_path: comprovanteNome,
        }
        await api.put(`/habitualidade/${editId}`, payload)
        setAlert({ type: 'success', message: 'Habitualidade atualizada!' })
      } else {
        // Criar multiplos eventos
        const base: Record<string, unknown> = {
          tipo_evento: tipoEvento,
          desta_entidade: destaEntidade,
        }
        if (isAdmin && selAtirador) base.idatirador = selAtirador
        for (const ev of eventos) {
          const payload = {
            ...base,
            evento: ev.evento || null,
            nivel: ev.nivel || null,
            data_inicio: ev.data_inicio || null,
            data_fim: ev.data_fim || null,
            grupo: ev.grupo || null,
            modalidade: ev.modalidade || null,
            quantidade_armas: ev.quantidade_armas ? parseInt(ev.quantidade_armas) : null,
            idarma: ev.idarma ? parseInt(ev.idarma) : null,
            qtd_municao: ev.qtd_municao ? parseInt(ev.qtd_municao) : null,
            classificacao: ev.classificacao ? parseInt(ev.classificacao) : null,
            data_participacao: ev.data_participacao || null,
            hora_participacao: ev.hora_participacao || null,
            comprovante_path: comprovanteNome,
          }
          await api.post('/habitualidade', payload)
        }
        setAlert({ type: 'success', message: `${eventos.length} habitualidade(s) salva(s) com sucesso!` })
      }
      resetForm()
      await loadHabitualidades()
    } catch (err: any) {
      setAlert({ type: 'error', message: err?.response?.data?.error || 'Erro ao salvar' })
    } finally {
      setSalvando(false)
    }
  }

  function formatData(dt: string | null) {
    if (!dt) return '—'
    try { return new Date(dt + 'T12:00:00').toLocaleDateString('pt-BR') } catch { return dt }
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title="Habitualidade"
        description="Lance treinos e provas avulsas (fora do sistema de campeonatos) para compor o Anexo E"
      />

      {alert && <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />}

      {/* Selecao de atirador (admin) */}
      {isAdmin && (
        <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 p-4 shadow-sm space-y-3">
          <h3 className="font-semibold text-compumaq-texto">Selecionar Atirador</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Buscar por nome (3+ letras)</label>
              <input
                type="text"
                className={inputClass}
                placeholder="Digite o nome do atirador..."
                value={searchAtirador}
                onChange={e => setSearchAtirador(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Atirador</label>
              <select
                className={selectClass}
                value={selAtirador}
                onChange={e => {
                  setSelAtirador(e.target.value ? parseInt(e.target.value) : '')
                  resetForm()
                }}
              >
                <option value="">Selecione...</option>
                {atiradores.map(a => (
                  <option key={a.idatirador} value={a.idatirador}>{a.nome} — {a.cpf}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Formulario de lancamento */}
      {podeCarregar && (
        <form onSubmit={handleSalvar} className="space-y-4">
          {/* Secao: Dados da Entidade */}
          <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 p-4 shadow-sm space-y-4">
            <h2 className="text-sm font-bold text-compumaq-doradoClaro uppercase tracking-wide border-b border-compumaq-dourado/25 pb-2">
              Dados da Entidade
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {/* Tipo Evento */}
              <div>
                <label className="block text-xs font-medium text-compumaq-textoSuave mb-2">Tipo Evento *</label>
                <div className="flex gap-4">
                  {(['prova', 'treino'] as const).map(t => (
                    <label key={t} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="radio"
                        name="tipo_evento"
                        value={t}
                        checked={tipoEvento === t}
                        onChange={() => setTipoEvento(t)}
                        className="w-4 h-4 text-compumaq-doradoClaro"
                      />
                      <span className="capitalize">{t === 'prova' ? 'Prova' : 'Treino'}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Habitualidades desta entidade? */}
              <div>
                <label className="block text-xs font-medium text-compumaq-textoSuave mb-2">Habitualidades desta entidade?</label>
                <div className="flex gap-4">
                  {[true, false].map(v => (
                    <label key={String(v)} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="radio"
                        name="desta_entidade"
                        checked={destaEntidade === v}
                        onChange={() => setDestaEntidade(v)}
                        className="w-4 h-4 text-compumaq-doradoClaro"
                      />
                      <span>{v ? 'Sim' : 'Nao'}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Comprovante */}
              <div>
                <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Comprovante de Participacao</label>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={e => setComprovante(e.target.files?.[0] ?? null)}
                  className="block w-full text-sm text-compumaq-textoSuave file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:text-xs file:font-medium file:bg-compumaq-dourado/10 file:text-compumaq-doradoClaro hover:file:bg-compumaq-dourado/15 cursor-pointer"
                />
                {comprovante && (
                  <p className="text-xs text-compumaq-textoSuave mt-1 truncate">{comprovante.name}</p>
                )}
              </div>
            </div>
          </div>

          {/* Secao: Eventos que Participou */}
          <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 p-4 shadow-sm space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-bold text-compumaq-doradoClaro uppercase tracking-wide">
                Eventos que Participou
              </h2>
              {!editando && (
                <button
                  type="button"
                  onClick={addEvento}
                  className="flex items-center gap-1 text-xs font-medium text-compumaq-doradoClaro bg-compumaq-dourado/10 hover:bg-compumaq-dourado/15 px-3 py-1.5 rounded-lg transition-colors"
                >
                  + Adicionar Evento
                </button>
              )}
            </div>

            {eventos.map((ev, idx) => (
              <div key={idx} className="border border-compumaq-dourado/25 rounded-lg p-4 space-y-3 relative">
                {!editando && eventos.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeEvento(idx)}
                    className="absolute top-2 right-2 text-compumaq-textoSuave hover:text-red-500 text-lg leading-none"
                    title="Remover evento"
                  >
                    &times;
                  </button>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {/* Evento */}
                  <div className="sm:col-span-2 lg:col-span-3">
                    <Field label="Evento">
                      <input
                        type="text"
                        className={inputClass}
                        placeholder="Ex: XV Campeonato Brasileiro"
                        value={ev.evento}
                        onChange={e => setEvento(idx, 'evento', e.target.value)}
                      />
                    </Field>
                  </div>

                  {/* Nivel */}
                  <Field label="Nivel">
                    <select
                      className={selectClass}
                      value={ev.nivel}
                      onChange={e => setEvento(idx, 'nivel', e.target.value)}
                    >
                      <option value="">-- Selecionar --</option>
                      {NIVEIS.map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </Field>

                  {/* Inicio */}
                  <Field label="Inicio">
                    <input
                      type="date"
                      className={inputClass}
                      value={ev.data_inicio}
                      onChange={e => setEvento(idx, 'data_inicio', e.target.value)}
                    />
                  </Field>

                  {/* Fim */}
                  <Field label="Fim">
                    <input
                      type="date"
                      className={inputClass}
                      value={ev.data_fim}
                      onChange={e => setEvento(idx, 'data_fim', e.target.value)}
                    />
                  </Field>

                  {/* Grupo */}
                  <Field label="Grupo">
                    <input
                      type="text"
                      className={inputClass}
                      placeholder="Ex: Pistola, Fuzil..."
                      value={ev.grupo}
                      onChange={e => setEvento(idx, 'grupo', e.target.value)}
                    />
                  </Field>

                  {/* Modalidade */}
                  <Field label="Modalidade">
                    <input
                      type="text"
                      className={inputClass}
                      placeholder="Ex: IPSC Standard..."
                      value={ev.modalidade}
                      onChange={e => setEvento(idx, 'modalidade', e.target.value)}
                    />
                  </Field>

                  {/* Quantidade Armas */}
                  <Field label="Qtd. Armas">
                    <select
                      className={selectClass}
                      value={ev.quantidade_armas}
                      onChange={e => setEvento(idx, 'quantidade_armas', e.target.value)}
                    >
                      {[1,2,3,4,5].map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </Field>

                  {/* Arma */}
                  <div className="sm:col-span-2">
                    <Field label="Arma (do atirador)">
                      <select
                        className={selectClass}
                        value={ev.idarma}
                        onChange={e => setEvento(idx, 'idarma', e.target.value)}
                      >
                        <option value="">-- Selecionar arma --</option>
                        {armas.map(a => (
                          <option key={a.idarma} value={a.idarma}>
                            {a.tipo} {a.marca} {a.modelo} {a.calibre}{a.numero_sigma ? ` — SIGMA: ${a.numero_sigma}` : ''}
                          </option>
                        ))}
                      </select>
                      {armas.length === 0 && (
                        <p className="text-xs text-amber-600 mt-1">
                          Nenhuma arma cadastrada. Cadastre em "Minhas Armas" primeiro.
                        </p>
                      )}
                    </Field>
                  </div>

                  {/* Qtd Municao */}
                  <Field label="Qtd. Municao">
                    <input
                      type="number"
                      min="0"
                      className={inputClass}
                      placeholder="0"
                      value={ev.qtd_municao}
                      onChange={e => setEvento(idx, 'qtd_municao', e.target.value)}
                    />
                  </Field>

                  {/* Classificacao */}
                  <Field label="Classificacao (posicao)">
                    <input
                      type="number"
                      min="1"
                      className={inputClass}
                      placeholder="Ex: 1"
                      value={ev.classificacao}
                      onChange={e => setEvento(idx, 'classificacao', e.target.value)}
                    />
                  </Field>

                  {/* Data Participacao */}
                  <Field label="Data Participacao">
                    <input
                      type="date"
                      className={inputClass}
                      value={ev.data_participacao}
                      onChange={e => setEvento(idx, 'data_participacao', e.target.value)}
                    />
                  </Field>

                  {/* Hora Participacao */}
                  <Field label="Hora Participacao">
                    <input
                      type="time"
                      className={inputClass}
                      value={ev.hora_participacao}
                      onChange={e => setEvento(idx, 'hora_participacao', e.target.value)}
                    />
                  </Field>
                </div>
              </div>
            ))}

            <div className="flex flex-wrap gap-3 pt-2">
              {editando && (
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2.5 border border-compumaq-dourado/25 rounded-lg text-sm font-medium text-compumaq-texto hover:bg-compumaq-preto"
                >
                  Cancelar
                </button>
              )}
              <button
                type="submit"
                disabled={salvando}
                className="px-6 py-2.5 bg-compumaq-dourado text-compumaq-texto rounded-lg text-sm font-semibold hover:bg-compumaq-doradoClaro disabled:opacity-50 transition-colors"
              >
                {salvando ? 'Salvando...' : editando ? 'Atualizar Habitualidade' : 'Salvar Habitualidade(s)'}
              </button>
            </div>
          </div>
        </form>
      )}

      {/* Lista de habitualidades cadastradas */}
      {podeCarregar && (
        <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 shadow-sm">
          <div className="px-4 py-3 border-b border-compumaq-dourado/25 flex flex-wrap items-center justify-between gap-3">
            <h3 className="font-semibold text-compumaq-texto">Habitualidades Lancadas</h3>
            <div className="flex items-center gap-2">
              <label className="text-xs text-compumaq-textoSuave">Ano:</label>
              <select
                className="px-3 py-1.5 border border-compumaq-dourado/25 rounded-lg text-sm bg-compumaq-pretoSuave focus:outline-none focus:ring-2 focus:ring-compumaq-dourado"
                value={ano}
                onChange={e => setAno(parseInt(e.target.value))}
              >
                {[0,1,2,3,4].map(off => {
                  const y = new Date().getFullYear() - off
                  return <option key={y} value={y}>{y}</option>
                })}
              </select>
            </div>
          </div>

          {loading ? (
            <div className="p-12 flex justify-center"><Spinner size="lg" /></div>
          ) : habitualidades.length === 0 ? (
            <div className="p-12 text-center text-compumaq-textoSuave">
              <p className="font-medium">Nenhuma habitualidade cadastrada para {ano}.</p>
              <p className="text-sm mt-1">Use o formulario acima para lancar treinos e provas avulsas.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-compumaq-preto text-xs">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium">Tipo</th>
                    <th className="text-left px-3 py-2 font-medium">Evento</th>
                    <th className="text-left px-3 py-2 font-medium">Nivel</th>
                    <th className="text-left px-3 py-2 font-medium">Modalidade</th>
                    <th className="text-left px-3 py-2 font-medium">Arma</th>
                    <th className="text-left px-3 py-2 font-medium">Qtd Mun.</th>
                    <th className="text-left px-3 py-2 font-medium">Classif.</th>
                    <th className="text-left px-3 py-2 font-medium">Data/Hora</th>
                    <th className="text-right px-3 py-2 font-medium">Acoes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-compumaq-dourado/25">
                  {habitualidades.map(hab => (
                    <tr key={hab.idhabitualidade} className="hover:bg-compumaq-preto">
                      <td className="px-3 py-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          hab.tipo_evento === 'prova'
                            ? 'bg-compumaq-dourado/15 text-compumaq-doradoClaro'
                            : 'bg-green-100 text-green-700'
                        }`}>
                          {hab.tipo_evento === 'prova' ? 'Prova' : 'Treino'}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <p className="font-medium text-compumaq-texto max-w-[180px] truncate">{hab.evento ?? '—'}</p>
                        {hab.desta_entidade && (
                          <span className="text-xs text-compumaq-textoSuave">Desta entidade</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-compumaq-textoSuave">{hab.nivel ?? '—'}</td>
                      <td className="px-3 py-2 text-compumaq-textoSuave max-w-[120px] truncate">{hab.modalidade ?? '—'}</td>
                      <td className="px-3 py-2 text-compumaq-textoSuave text-xs max-w-[150px]">
                        {hab.arma_marca
                          ? `${hab.arma_tipo ?? ''} ${hab.arma_marca} ${hab.arma_modelo ?? ''} ${hab.arma_calibre ?? ''}`.trim()
                          : '—'
                        }
                      </td>
                      <td className="px-3 py-2 text-compumaq-textoSuave">{hab.qtd_municao ?? '—'}</td>
                      <td className="px-3 py-2 text-compumaq-textoSuave">
                        {hab.classificacao ? `${hab.classificacao}o` : '—'}
                      </td>
                      <td className="px-3 py-2 text-compumaq-textoSuave text-xs whitespace-nowrap">
                        {formatData(hab.data_participacao)}
                        {hab.hora_participacao && (
                          <span className="text-compumaq-textoSuave"> {hab.hora_participacao.slice(0, 5)}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => openEdit(hab)}
                            className="text-xs text-compumaq-doradoClaro hover:text-compumaq-doradoClaro font-medium"
                          >
                            Editar
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(hab.idhabitualidade)}
                            className="text-xs text-red-500 hover:text-red-700 font-medium"
                          >
                            Excluir
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {!podeCarregar && isAdmin && (
        <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 p-12 text-center text-compumaq-textoSuave shadow-sm">
          <p className="font-medium">Selecione um atirador acima para gerenciar habitualidades.</p>
        </div>
      )}
    </div>
  )
}
